import React, { useEffect, useRef, useState } from "react";
import { Conversation } from "@twilio/conversations/lib/conversation";
import { AiOutlineFileAdd } from "react-icons/ai";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Textarea
} from "@chakra-ui/react";

import { isMobile } from "utils/session";

interface ChatInputProps {
  conversation: Conversation;
  isChatWindowOpen: boolean;
  toggleGameLiftStreamsInput: any;
}

const ALLOWED_FILE_TYPES =
  "audio/*, image/*, text/*, video/*, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document .xslx, .ppt, .pdf, .key, .svg, .csv";

const Input: React.FC<ChatInputProps> = ({
  conversation,
  isChatWindowOpen,
  toggleGameLiftStreamsInput
}: ChatInputProps) => {
  const [messageBody, setMessageBody] = useState("");
  const [isSendingFile, setIsSendingFile] = useState(false);
  const [fileSendError, setFileSendError] = useState<string | null>(null);
  const isValidMessage = /\S/.test(messageBody);
  const textInputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (fileSendError) {
      console.log(fileSendError);
    }
  }, [fileSendError]);

  useEffect(() => {
    if (isChatWindowOpen) {
      // When the chat window is opened, we will focus on the text input.
      // This is so the user doesn't have to click on it to begin typing a message.
      textInputRef.current?.focus();
    }
  }, [isChatWindowOpen]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageBody(event.target.value);
  };

  // ensures pressing enter + shift creates a new line, so that enter on its own only sends the message:
  const handleReturnKeyPress = (event: React.KeyboardEvent) => {
    if (!isMobile && event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage(messageBody);
    }
  };

  const handleSendMessage = (message: string) => {
    if (isValidMessage) {
      conversation.sendMessage(message.trim());
      setMessageBody("");
    }
  };

  const handleSendFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const formData = new FormData();
      formData.append("userfile", file);
      setIsSendingFile(true);
      setFileSendError(null);
      conversation
        .sendMessage(formData)
        .catch(e => {
          if (e.code === 413) {
            setFileSendError(
              "File size is too large. Maximum file size is 150MB."
            );
          } else {
            setFileSendError(
              "There was a problem uploading the file. Please try again."
            );
          }
          console.log("Problem sending file: ", e);
        })
        .finally(() => {
          setIsSendingFile(false);
        });
    }
  };

  return (
    <Grid templateRows="repeat(2, fit-content(100%))" gap={2}>
      <Grid alignItems="flex-end" justifyContent="flex-end" flexWrap="nowrap">
        {/* Since the file input element is invisible, we can hardcode an empty string as its value.
        This allows users to upload the same file multiple times. */}
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          onChange={handleSendFile}
          value={""}
          accept={ALLOWED_FILE_TYPES}
        />
        <Grid hidden>
          <Box>
            <IconButton
              aria-label="Send File"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSendingFile}
              icon={<AiOutlineFileAdd />}
            />
            {isSendingFile && <CircularProgress size={24} />}
          </Box>
        </Grid>
      </Grid>
      <Grid templateColumns="1fr 25%" gap={2}>
        {/*
        Here we add the "isTextareaFocused" class when the user is focused on the TextareaAutosize component.
        This helps to ensure a consistent appearance across all browsers. Adding padding to the TextareaAutosize
        component does not work well in Firefox. See: https://github.com/twilio/twilio-video-app-react/issues/498
        */}
        <Textarea
          aria-label="chat input"
          fontSize="16px"
          minHeight="auto"
          onKeyPress={handleReturnKeyPress}
          onChange={handleChange}
          placeholder="Type a message..."
          ref={textInputRef}
          resize="none"
          value={messageBody}
          data-cy-chat-input
          onClick={() => toggleGameLiftStreamsInput(false)}
        />
        <Button
          onClick={() => handleSendMessage(messageBody)}
          variant="blue"
          disabled={!isValidMessage}
          data-cy-send-message-button
          size="sm"
        >
          Send
        </Button>
      </Grid>
    </Grid>
  );
};

export default Input;
